import React, { useMemo, useState } from "react";
import { Avatar } from "primereact/avatar";
import PropTypes from "prop-types";
import Attachments from "./Attachments";
import "./conversations.scss";
import {
  downloadFileHandler,
  resolveDisplayFileName,
  getOriginalFileName,
  getBackendFileName,
  openPreviewInNewTab,
} from "../../../utils/fileUtils";
import Categories from "./utils/categories.json";
import Menu from "~/components/menu/Menu";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";

const Message = ({
  messageInfo,
  formatDate,
  conversation,
  deletedAt,
  setDeleteModal,
  setSelectedMessage,
  relatedType = "",
  processdata = {},
  serviceData: serviceDataProp = {},
  allConversationAttachments = [],
}) => {
  const { id: userId, role: userRole } = useSelector((state) => state.login);
  const processdataFromRedux = useSelector(
    (state) => state.process?.processdata,
  );
  const effectiveProcessdata =
    processdata && Object.keys(processdata).length > 0
      ? processdata
      : processdataFromRedux || {};
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const getInitials = (fullName) => {
    const nameArray = fullName.split(" ");
    const firstInitial = nameArray[0] ? nameArray[0].charAt(0) : "";
    const lastInitial =
      nameArray.length > 1 ? nameArray[nameArray.length - 1].charAt(0) : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const getUserDisplayName = (userData) => {
    if (!userData) return "Unknown User";
    const firstName = userData.first_name || "";
    const lastName = userData.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unknown User";
  };

  const getRoleDisplay = (userData) => {
    if (!userData?.role) return "";
    return role[userData.role] || userData.role;
  };

  const role = {
    GXM_ADMIN: "GXM Admin",
    CUSTOMER: "Customer",
    LSP_USER: "Local Team",
    GXM_USER: "GXM User",
    GXM_MANAGER: "GXM Manager",
    LSP_MANAGER: "Local Team Manager",
    CUSTOMER_MANAGER: "Customer Manager",
  };

  const menuItems = useMemo(
    () => [
      {
        label: "Delete Message",
        icon: "pi pi-trash",
        command: () => {
          // Remove commentId from URL when delete modal opens
          const params = new URLSearchParams(location.search);
          if (params.has("commentId")) {
            params.delete("commentId");
            params.delete("commentType");
            const newSearch = params.toString();
            const newPath = newSearch
              ? `${location.pathname}?${newSearch}`
              : location.pathname;
            navigate(newPath, { replace: true });
          }

          setDeleteModal(true);
          setSelectedMessage(conversation);
        },
      },
    ],
    [conversation, setDeleteModal, setSelectedMessage, navigate, location],
  );

  const deletedUser = messageInfo?.updatedBy || messageInfo?.userData;
  const [attachmentOverrides, setAttachmentOverrides] = useState({});

  const getSubCategoryName = (categoryCode, subCategoryCode) => {
    const cat = Categories?.find((c) => c.code === categoryCode);
    const sub = cat?.subCategory?.find((s) => s.code === subCategoryCode);
    return sub?.name ?? subCategoryCode ?? "-";
  };

  const getAttachmentKey = (file, index) =>
    file?.blobName || file?.blob_url || file?.id || `${file?.name}-${index}`;

  const mergedAttachmentData = useMemo(
    () =>
      (messageInfo?.attachmentData || []).map((file, index) => {
        const key = getAttachmentKey(file, index);
        return attachmentOverrides[key]
          ? { ...file, ...attachmentOverrides[key] }
          : file;
      }),
    [messageInfo?.attachmentData, attachmentOverrides],
  );

  const isPayrollService =
    (relatedType === "PROCESS" || relatedType === "REQUEST") &&
    (effectiveProcessdata?.serviceData?.name === "Payroll" ||
      serviceDataProp?.name === "Payroll");

  const getFormattedAttachmentName = (file, attachmentKey) => {
    const scope =
      allConversationAttachments.length > 0
        ? allConversationAttachments
        : mergedAttachmentData;

    // For Payroll services, use backend name unless the category was locally
    if (isPayrollService) {
      const override = attachmentKey
        ? attachmentOverrides[attachmentKey]
        : null;
      const hasLocalCategoryUpdate =
        override?.category !== undefined || override?.subCategory !== undefined;
      if (!hasLocalCategoryUpdate) {
        return getBackendFileName(file);
      }
    }

    return resolveDisplayFileName(file, scope, getSubCategoryName);
  };

  const getContainer = (blobUrl) =>
    blobUrl?.includes("/ess") ? "ess" : "documents";

  const openPreview = (file) =>
    openPreviewInNewTab(
      { objectURL: file?.blob_url },
      getContainer(file?.blob_url),
    );

  return (
    <div className="message-container">
      <Avatar
        label={getInitials(getUserDisplayName(messageInfo?.userData))}
        shape="circle"
        className="avatar"
      />
      <div className="message-content">
        <div className="meta-info-container">
          <div className="meta-info">
            <p className="user-name">
              {getUserDisplayName(messageInfo?.userData)}
              {getRoleDisplay(messageInfo?.userData) &&
                ` (${getRoleDisplay(messageInfo?.userData)})`}
            </p>
            <p className="date-time">{formatDate(messageInfo?.createdAt)}</p>
          </div>
          {!deletedAt &&
            (userId === messageInfo?.userData?.id ||
              userRole?.startsWith("GXM")) && (
              <div className="menu-container">
                <Menu menuItems={menuItems} />
              </div>
            )}
        </div>
        {messageInfo?.content && !deletedAt && (
          <p className="message">{messageInfo?.content}</p>
        )}
        {deletedAt && (
          <p className="message deleted-msg">
            Message removed by {getUserDisplayName(deletedUser)}
            {getRoleDisplay(deletedUser) && ` (${getRoleDisplay(deletedUser)})`}
            .
          </p>
        )}
        {mergedAttachmentData.length > 0 &&
          !deletedAt &&
          mergedAttachmentData.map((file, index) => {
            const attachmentKey = getAttachmentKey(file, index);
            return (
              <Attachments
                key={attachmentKey}
                file={{
                  ...file,
                  createdByData: messageInfo?.userData,
                  createdAt: messageInfo?.updatedAt,
                }}
                displayName={getFormattedAttachmentName(file, attachmentKey)}
                onAttachmentUpdate={(updatedFile) => {
                  setAttachmentOverrides((prev) => ({
                    ...prev,
                    [attachmentKey]: {
                      ...(prev[attachmentKey] || {}),
                      ...updatedFile,
                    },
                  }));
                }}
                icon="pi-download"
                progressBar={false}
                fileHandler={() => downloadFileHandler(file)}
                openPreviewHandler={() => openPreview(file)}
                showPreviewIcon={false}
                showDescriptionIcon={true}
                id={id}
                relatedType={relatedType}
              />
            );
          })}
      </div>
    </div>
  );
};

Message.propTypes = {
  messageInfo: PropTypes.any,
  formatDate: PropTypes.func,
  conversation: PropTypes.object,
  deletedAt: PropTypes.string,
  setDeleteModal: PropTypes.func,
  setSelectedMessage: PropTypes.func,
  relatedType: PropTypes.string,
  processdata: PropTypes.object,
  serviceData: PropTypes.object,
  allConversationAttachments: PropTypes.arrayOf(PropTypes.object),
};

export default Message;
